import { create } from 'zustand';
import {
  resolutionApi,
  type ConfidenceBin,
  type CoverageSummaryRow,
  type EntityTypeConfig,
  type MergeSignalsResponse,
  type OverMergeCandidate,
  type ThresholdRecommendation,
  type UnderMergeCandidate,
} from '@/services/api';

interface QualityState {
  coverage: CoverageSummaryRow[];
  coverageTrend: Array<{ day: string; count: number }>;
  confidence: ConfidenceBin[];
  confidenceEntityType: string;
  mergeSignals: MergeSignalsResponse | null;
  mergeSignalEntityType: string;
  mergeSigma: number;
  mergeSimFloor: number;
  recommendations: ThresholdRecommendation[];
  showAppliedDismissed: boolean;
  entityTypeConfigs: EntityTypeConfig[];
  loading: boolean;
  trendLoading: boolean;
  confidenceLoading: boolean;
  signalsLoading: boolean;
  recsLoading: boolean;
  analyzing: boolean;
  actingId: string | null;
  selectedOverMerge: OverMergeCandidate | null;
  selectedUnderMerge: UnderMergeCandidate | null;
  selectedRecommendation: ThresholdRecommendation | null;
  analyzeStatus: string | null;
  error: string | null;

  fetchCoverage: () => Promise<void>;
  fetchCoverageTrend: (days?: number) => Promise<void>;
  fetchConfidence: (entityType?: string) => Promise<void>;
  fetchMergeSignals: (params?: {
    entity_type?: string;
    sigma_threshold?: number;
    similarity_floor?: number;
  }) => Promise<void>;
  fetchRecommendations: (only_pending?: boolean) => Promise<void>;
  fetchEntityTypeConfigs: () => Promise<void>;
  setConfidenceEntityType: (et: string) => void;
  setMergeSignalParams: (params: {
    entity_type?: string;
    sigma_threshold?: number;
    similarity_floor?: number;
  }) => void;
  setShowAppliedDismissed: (v: boolean) => void;
  selectOverMerge: (c: OverMergeCandidate | null) => void;
  selectUnderMerge: (c: UnderMergeCandidate | null) => void;
  selectRecommendation: (r: ThresholdRecommendation | null) => void;
  runAnalysis: (actorEmail?: string) => Promise<void>;
  applyRecommendation: (id: string, actorEmail?: string) => Promise<void>;
  dismissRecommendation: (id: string, actorEmail?: string) => Promise<void>;
  triggerMergeForUnderMerge: (candidate: UnderMergeCandidate) => Promise<void>;
}

export const useQualityDashboardStore = create<QualityState>((set, get) => ({
  coverage: [],
  coverageTrend: [],
  confidence: [],
  confidenceEntityType: '',
  mergeSignals: null,
  mergeSignalEntityType: '',
  mergeSigma: 2.0,
  mergeSimFloor: 0.9,
  recommendations: [],
  showAppliedDismissed: false,
  entityTypeConfigs: [],
  loading: false,
  trendLoading: false,
  confidenceLoading: false,
  signalsLoading: false,
  recsLoading: false,
  analyzing: false,
  actingId: null,
  selectedOverMerge: null,
  selectedUnderMerge: null,
  selectedRecommendation: null,
  analyzeStatus: null,
  error: null,

  fetchCoverage: async () => {
    set({ loading: true });
    try {
      const res = await resolutionApi.getQualityCoverage();
      set({ coverage: res.coverage, loading: false });
    } catch (err) {
      set({ loading: false, error: (err as Error).message });
    }
  },

  fetchCoverageTrend: async (days = 30) => {
    set({ trendLoading: true });
    try {
      const res = await resolutionApi.getQualityCoverageTrend(days);
      set({ coverageTrend: res.series, trendLoading: false });
    } catch {
      set({ coverageTrend: [], trendLoading: false });
    }
  },

  fetchConfidence: async (entityType) => {
    const et = entityType ?? get().confidenceEntityType;
    set({ confidenceLoading: true, confidenceEntityType: et });
    try {
      const res = await resolutionApi.getQualityConfidence(et || undefined, 10);
      set({ confidence: res.bins, confidenceLoading: false });
    } catch {
      set({ confidence: [], confidenceLoading: false });
    }
  },

  fetchMergeSignals: async (params) => {
    const merged = {
      entity_type: params?.entity_type ?? get().mergeSignalEntityType,
      sigma_threshold: params?.sigma_threshold ?? get().mergeSigma,
      similarity_floor: params?.similarity_floor ?? get().mergeSimFloor,
    };
    set({
      signalsLoading: true,
      mergeSignalEntityType: merged.entity_type,
      mergeSigma: merged.sigma_threshold,
      mergeSimFloor: merged.similarity_floor,
    });
    try {
      const res = await resolutionApi.getMergeSignals({
        entity_type: merged.entity_type || undefined,
        sigma_threshold: merged.sigma_threshold,
        similarity_floor: merged.similarity_floor,
      });
      set({ mergeSignals: res, signalsLoading: false });
    } catch (err) {
      set({
        signalsLoading: false,
        mergeSignals: { over_merge: [], under_merge: [], params: merged },
        error: (err as Error).message,
      });
    }
  },

  fetchRecommendations: async (only_pending) => {
    const showApplied = get().showAppliedDismissed;
    const pending = only_pending ?? !showApplied;
    set({ recsLoading: true });
    try {
      const res = await resolutionApi.listThresholdRecommendations({
        only_pending: pending,
        limit: 100,
      });
      set({ recommendations: res.recommendations, recsLoading: false });
    } catch (err) {
      set({
        recommendations: [],
        recsLoading: false,
        error: (err as Error).message,
      });
    }
  },

  fetchEntityTypeConfigs: async () => {
    try {
      const res = await resolutionApi.listEntityTypeConfigs();
      set({ entityTypeConfigs: res.configs });
    } catch {
      set({ entityTypeConfigs: [] });
    }
  },

  setConfidenceEntityType: (et) => {
    set({ confidenceEntityType: et });
    get().fetchConfidence(et);
  },

  setMergeSignalParams: (params) => {
    get().fetchMergeSignals(params);
  },

  setShowAppliedDismissed: (v) => {
    set({ showAppliedDismissed: v });
    get().fetchRecommendations();
  },

  selectOverMerge: (c) => set({ selectedOverMerge: c }),
  selectUnderMerge: (c) => set({ selectedUnderMerge: c }),
  selectRecommendation: (r) => set({ selectedRecommendation: r }),

  runAnalysis: async (actorEmail) => {
    set({ analyzing: true, analyzeStatus: null });
    try {
      const res = await resolutionApi.analyzeVerdicts({
        persist: true,
      });
      set({
        analyzeStatus: `Produced ${res.count} recommendation${res.count === 1 ? '' : 's'} (actor: ${
          actorEmail ?? 'anonymous'
        })`,
      });
      // Refresh the recommendations list so new ones show up.
      await get().fetchRecommendations();
    } catch (err) {
      set({ analyzeStatus: (err as Error).message });
    } finally {
      set({ analyzing: false });
    }
  },

  applyRecommendation: async (id, actorEmail) => {
    set({ actingId: id });
    try {
      await resolutionApi.actOnThresholdRecommendation(
        id,
        { action: 'apply', actor: actorEmail },
        actorEmail,
      );
      set((s) => ({
        recommendations: s.showAppliedDismissed
          ? s.recommendations
          : s.recommendations.filter((r) => r.id !== id),
        selectedRecommendation:
          s.selectedRecommendation?.id === id ? null : s.selectedRecommendation,
      }));
      await get().fetchEntityTypeConfigs();
    } catch (err) {
      set({ error: (err as Error).message });
    } finally {
      set({ actingId: null });
    }
  },

  dismissRecommendation: async (id, actorEmail) => {
    set({ actingId: id });
    try {
      await resolutionApi.actOnThresholdRecommendation(
        id,
        { action: 'dismiss', actor: actorEmail },
        actorEmail,
      );
      set((s) => ({
        recommendations: s.showAppliedDismissed
          ? s.recommendations
          : s.recommendations.filter((r) => r.id !== id),
        selectedRecommendation:
          s.selectedRecommendation?.id === id ? null : s.selectedRecommendation,
      }));
    } catch (err) {
      set({ error: (err as Error).message });
    } finally {
      set({ actingId: null });
    }
  },

  triggerMergeForUnderMerge: async (candidate) => {
    // Run the LLM resolution pipeline for the entity type; the pair will get
    // picked up in the next clustering pass.
    set({ actingId: `${candidate.entity_a_id}:${candidate.entity_b_id}` });
    try {
      await resolutionApi.runResolution({
        entity_type: candidate.entity_type,
        similarity_threshold: 0.8,
        generate_embeddings: false,
      });
      set({
        analyzeStatus: `Triggered resolution on ${candidate.entity_type} — refresh signals in a few seconds.`,
      });
    } catch (err) {
      set({ error: (err as Error).message });
    } finally {
      set({ actingId: null });
    }
  },
}));
